import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { orderAPI } from '../lib/api';

const AccountPage = () => {
    const { user, logout } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchOrders = async () => {
            try {
                const res = await orderAPI.getAll();
                setOrders(res.data);
            } catch (error) {
                console.error('Failed to fetch orders:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [user, navigate]);

    if (!user) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed':
            case 'paid':
                return 'bg-emerald-100 text-emerald-700';
            case 'pending':
                return 'bg-amber-100 text-amber-700';
            case 'cancelled':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-stone-100 text-stone-700';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'confirmed': return t('confirmed');
            case 'paid': return t('paid');
            case 'pending': return t('pending');
            case 'cancelled': return t('cancelled');
            default: return status;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('no-NO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="page-transition" data-testid="account-page">
            <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-12 md:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-12">
                        <div>
                            <h1 className="font-display text-4xl md:text-5xl font-light mb-2">
                                {t('myAccount')}
                            </h1>
                            <p className="text-stone-500">{user.email}</p>
                        </div>
                        <button
                            onClick={logout}
                            className="text-sm text-stone-500 hover:text-stone-900 transition-colors"
                            data-testid="logout-btn"
                        >
                            {t('signOut')}
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="bg-stone-50 p-8 mb-12">
                        <h2 className="font-display text-lg mb-4">{t('accountDetails')}</h2>
                        <div className="grid grid-cols-2 gap-6 text-sm">
                            <div>
                                <span className="text-stone-500">{t('name')}</span>
                                <p className="font-medium" data-testid="user-name">{user.name}</p>
                            </div>
                            <div>
                                <span className="text-stone-500">{t('email')}</span>
                                <p className="font-medium" data-testid="user-email">{user.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* Orders */}
                    <div>
                        <h2 className="font-display text-2xl mb-8">{t('orderHistory')}</h2>

                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-24 bg-stone-100 animate-pulse" />
                                ))}
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-16 bg-stone-50">
                                <Package className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                                <p className="text-stone-500">{t('noOrdersYet')}</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map((order, index) => (
                                    <motion.div
                                        key={order.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        className="border border-stone-200 p-6 hover:border-stone-300 transition-colors"
                                        data-testid={`order-${index}`}
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-4 mb-2">
                                                    <p className="font-mono text-sm text-stone-500">
                                                        #{order.id.slice(0, 8)}
                                                    </p>
                                                    <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusColor(order.status)}`}>
                                                        {getStatusLabel(order.status)}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-stone-500">
                                                    {formatDate(order.created_at)} · {order.items.length} {order.items.length !== 1 ? t('items') : t('item')}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-6">
                                                <p className="font-mono text-lg">{order.total},- kr</p>
                                            </div>
                                        </div>

                                        {/* Order Items Preview */}
                                        <div className="mt-4 pt-4 border-t border-stone-100">
                                            <div className="flex flex-wrap gap-2">
                                                {order.items.map((item, i) => (
                                                    <span 
                                                        key={i}
                                                        className="text-xs text-stone-500 bg-stone-100 px-3 py-1"
                                                    >
                                                        {item.product_name} × {item.quantity}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default AccountPage;
